"use client"

import { accountText, type LocaleProps } from "../../personal-web"
import type { AndroidBrand } from "../types"

type Brand = {
  id: AndroidBrand
  labelKey: string
  /** Single-character mark — safer than guessing trademarked logos. */
  mark: string
}

const BRANDS: Brand[] = [
  { id: "samsung", labelKey: "install.brand.samsung", mark: "S" },
  { id: "pixel", labelKey: "install.brand.pixel", mark: "G" },
  // "…" reads as "and the rest" without claiming any specific manufacturer.
  { id: "generic", labelKey: "install.brand.other", mark: "…" },
]

type Props = LocaleProps & {
  brand: AndroidBrand
  onChange: (brand: AndroidBrand) => void
}

/**
 * Three brand pills (Samsung / Pixel / Other) all using the same pill style.
 * Active state changes only colour — no checkmark icon — so the row stays
 * quiet. "Other" routes to the generic Android install flow that pattern-
 * matches Settings paths across OPPO / Vivo / Huawei / Xiaomi / OnePlus etc.
 */
export function BrandPills({ lng, brand, onChange }: Props) {
  return (
    <div
      role="radiogroup"
      aria-label={accountText(lng, "install.brand.ariaLabel")}
      className="grid grid-cols-3 gap-2"
    >
      {BRANDS.map((b) => {
        const active = brand === b.id
        return (
          <button
            key={b.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(b.id)}
            className={`flex min-h-12 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 ${
              active
                ? "border-orange-400 bg-orange-50 text-orange-700"
                : "border-stone-200 bg-white text-stone-700 hover:border-stone-300"
            }`}
          >
            <span
              aria-hidden
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-extrabold transition-colors ${
                active
                  ? "bg-orange-500 text-white"
                  : "bg-stone-100 text-stone-700"
              }`}
            >
              {b.mark}
            </span>
            <span className="truncate">{accountText(lng, b.labelKey)}</span>
          </button>
        )
      })}
    </div>
  )
}
